import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Redirect, RouteComponentProps, useParams } from 'react-router-dom';
import { navigationService, connectToAnExpertService } from 'src/config/rootService';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { useAnalytics } from '@modernhealth/web-analytics';
import { PAMMCareOptionsForm } from '../components/PAMMCareOptionsForm';
import { PAMMCareOptionsSuccess } from '../components/PAMMCareOptionsSuccess';
import { ReactComponent as ChangeIllustration } from 'src/public/ds/illustrations/marginalia-delivery.svg';
import { ReactComponent as RemoveIllustration } from 'src/public/ds/illustrations/marginalia-done.svg';

export enum PAMMCareOptions {
    Change = 'change',
    ChangeSuccess = 'changeSuccess',
    Remove = 'remove',
    RemoveSuccess = 'removeSuccess',
}

export interface Params {
    option: PAMMCareOptions;
    uuid?: string;
}

export const PAMMCareOptionsPage: FC<RouteComponentProps<Params, {}, Params>> = ({ history }) => {
    const { option, uuid } = useParams<Params>();
    const { t } = useTranslation();
    const analytics = useAnalytics();
    const user = useSelector(getUserInformation);

    const submitClickEventName = 'PAMM Care Options: Submit Request';
    const backClickEventName = 'PAMM Care Options: Back';

    if (!uuid) {
        return <Redirect to="/dashboard" />;
    }

    const navigateToHome = () => {
        navigationService.navigate('/dashboard');
    };
    const navigateToCareTeam = () => {
        navigationService.navigate('/care-team');
    };

    const onSubmitChange = (text: string) => {
        analytics.trackClick(submitClickEventName, { option: option });
        connectToAnExpertService.submitForm({
            userInfo: user,
            anythingElse: text,
            therapistRequested: false,
            psychiatristRequested: true,
            tags: ['pamm-match-change-request'],
        });
        navigationService.navigate(`/pamm-care-options/changeSuccess/${uuid}`);
    };

    const onSubmitRemoval = (text: string) => {
        analytics.trackClick(submitClickEventName, { option: option });
        connectToAnExpertService.submitForm({
            userInfo: user,
            anythingElse: text,
            therapistRequested: false,
            psychiatristRequested: true,
            tags: ['pamm-match-removal-request'],
        });
        navigationService.navigate(`/pamm-care-options/removeSuccess/${uuid}`);
    };

    const onBackClick = () => {
        analytics.trackClick(backClickEventName, { option: option });
        history.goBack();
    };

    switch (option) {
        case PAMMCareOptions.Change:
            return (
                <PAMMCareOptionsForm
                    title={t('care:pamm_care_options.change.title')}
                    description={t('shared:contact_us_modal.specific_requests_for_provider')}
                    secondButtonText={t('care:pamm_care_options.change.request')}
                    onBack={onBackClick}
                    onSubmit={onSubmitChange}
                    calloutMessage={t('providers:pamm_provider.request.callout.description')}
                />
            );
        case PAMMCareOptions.ChangeSuccess:
            return (
                <PAMMCareOptionsSuccess
                    title={t('providers:pamm_provider.request.success.change_title')}
                    descriptionKey="pamm_provider.request.success.change_description"
                    firstButtonText={t('providers:pamm_provider.request.success.back_to_care_team')}
                    secondButtonText={t('providers:pamm_provider.request.success.back_to_home')}
                    onFirstButtonClick={navigateToCareTeam}
                    onSecondButtonClick={navigateToHome}
                    Icon={ChangeIllustration}
                />
            );
        case PAMMCareOptions.Remove:
            return (
                <PAMMCareOptionsForm
                    title={t('care:pamm_care_options.remove.title')}
                    description={t('care:pamm_care_options.remove.description')}
                    secondButtonText={t('messaging:conversation.header.remove_provider')}
                    secondButtonVariant="destructive"
                    onBack={onBackClick}
                    onSubmit={onSubmitRemoval}
                />
            );
        case PAMMCareOptions.RemoveSuccess:
            return (
                <PAMMCareOptionsSuccess
                    title="Provider removal request sent"
                    descriptionKey="pamm_provider.request.success.remove_description"
                    firstButtonText={t('providers:pamm_provider.request.success.back_to_care_team')}
                    secondButtonText={t('providers:pamm_provider.request.success.back_to_home')}
                    onFirstButtonClick={navigateToCareTeam}
                    onSecondButtonClick={navigateToHome}
                    Icon={RemoveIllustration}
                />
            );
        default:
            return <Redirect to="/dashboard" />;
    }
};
